import { FC, useEffect, useState } from 'react';
import {
  Modal,
  DatePicker,
  DatePickerProps,
  TimePicker,
  Button as AntButton,
} from 'antd';
import Radio from '../../../../Radio';
import {
  ISendMessageReq,
  IStudentMessage,
  SendTimeDiv,
  SendType,
} from '../../../../../models/pushMessage.model';
import moment, { Moment } from 'moment';
import useNotification from '../../../../../hooks/useNotification';
import { RangePickerProps } from 'antd/es/date-picker';
import { IParent, IStudent } from '../../../../../models/students.model';
import ConfirmModal from '../../../../modals/ConfirmModal';
import { useTranslation } from 'react-i18next';
import Checkbox from '../../../../Checkbox';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/rootReducer';

interface IProps {
  data?: IStudentMessage;
  visible: boolean;
  parent?: IParent;
  student?: IStudent;
  onCancel(): void;

  onMultiOk(data: ISendMessageReq[]): void;
}

const SendMessageModal: FC<IProps> = ({
  data,
  visible,
  parent,
  student,
  onCancel,
  onMultiOk,
}) => {
  const { infoNotification } = useNotification();
  const { t } = useTranslation();
  const { campusData } = useSelector((root: RootState) => root.campusData);
  const { currentCampus } = useSelector((root: RootState) => root.auth);

  const [sendType, setSendType] = useState<SendType>('GE');
  const [content, setContent] = useState('');
  const [sendTimeDiv, setSendTimeDiv] = useState<SendTimeDiv>('IM');
  const [date, setDate] = useState<Moment | undefined>();
  const [time, setTime] = useState<Moment | undefined>();
  const [phone, setPhone] = useState<string>();
  const [sms, setSms] = useState(false);
  const [confirmReserveVisible, setConfirmReserveVisible] = useState(false);
  const [isParentSend, setIsParentSend] = useState(false);
  const [isStudentSend, setIsStudentSend] = useState(false);
  const [parentName, setParentName] = useState<string>();
  const [studentPhone, setStudentPhone] = useState<string>();

  useEffect(() => {
    if (parent) {
      if (parent.additionalParentSmsYn === 'Y') {
        setPhone(parent.additionalParentTelephone);
        setParentName(parent.additionalParentName);
      } else {
        setPhone(parent.mobilePhone);
        setParentName(parent.memberKorName);
      }
    }
  }, [parent]);

  useEffect(() => {
    if(student) {
      setStudentPhone(student.mobilePhone);
    }
  }, [student]);

  const onDateChange: DatePickerProps['onChange'] = (value: Moment | null) =>
    value && setDate(value);

  const onTimeChange = (time: Moment | null) => time && setTime(time);

  const onSubmitClick = () => {
    if(!isParentSend && !isStudentSend) return infoNotification(t('student.student.message60'));
    if (!content) return infoNotification(t('student.student.message56'));
    //학부모 선택
    if(isParentSend) {
      if (!phone) return infoNotification(t('system.school.message3'));
      if (phone.length < 11)
        return infoNotification(t('student.student.message47'));
    }
    //학생 선택
    if(isStudentSend){
      if (!studentPhone) return infoNotification(t('system.school.message3'));
      if (studentPhone.length < 11)
        return infoNotification(t('student.student.message47'));
    }
    if (sendTimeDiv === 'RE') {
      if (!date) return infoNotification(t('student.student.message16'));
      if (!time) return infoNotification(t('student.student.message17'));
    }

    if (sendTimeDiv === 'IM') {
      fnSubmit();
    } else if (sendTimeDiv === 'RE') {
      setConfirmReserveVisible(true);
    }
  };

  const fnSubmit = () => {
    const paramData: ISendMessageReq[] = [];

    const campus = campusData.find(
      ({ campusCode }) => campusCode === currentCampus,
    );

    if(student && campus) {
      if(isParentSend) {
        paramData.push({
          content,
          sendType,
          sendTimeDiv,
          ...(sendTimeDiv === 'RE' && {
            sendDateTime: `${date?.format('YYYY-MM-DD')} ${time?.format(
              'HH:mm',
            )}:00`,
          }),
          phone,
          sms,
          campusCode: campus.campusCode,
          memberCode: [student.studentCode],
          outgoingNumber: campus.telephoneNo
        });
      }
      if(isStudentSend) {
        paramData.push({
          content,
          sendType,
          sendTimeDiv,
          ...(sendTimeDiv === 'RE' && {
            sendDateTime: `${date?.format('YYYY-MM-DD')} ${time?.format(
              'HH:mm',
            )}:00`,
          }),
          phone:studentPhone,
          sms,
          campusCode: campus.campusCode,
          memberCode: [student.studentCode],
          outgoingNumber: campus.telephoneNo
        });
      }

      if(paramData.length > 0) {
        onMultiOk(paramData);
      }
    }
  }

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    const userCurrent = moment(moment().format('YYYY-MM-DD'));
    const dateCurrent = moment(current.format('YYYY-MM-DD'));
    return dateCurrent.isBefore(userCurrent);
  };

  const range = (start: number, end: number) => {
    const result = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  };

  const disabledHours = (): number[] => {
    const currentDate = moment(new Date());
    const hourArray = Array.from({ length: 24 }, (v, k) => k);
    if (date?.format('YYYY-MM-DD') === currentDate.format('YYYY-MM-DD')) {
      return hourArray.filter((v) => v < currentDate.hour());
    } else {
      return [];
    }
  }

  const disabledMinutes = (selectedHour: number): number[] => {
    if (selectedHour === new Date().getHours())
      return range(0, 60).slice(0, new Date().getMinutes());
    else return [];
  };

  useEffect(() => {
    if (data) {
      if (data.content.includes('(광고)') || data.content.includes('(Ad)')) setSendType('AD');
      else setSendType('GE');
      setContent(data.content);
    }
  }, [data]);
  
  return (
    <>
      <Modal
        title={t('student.student.button10')}
        visible={visible}
        width={600}
        onCancel={onCancel}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton disabled={Boolean(data)} className='btn-basic pink' onClick={onSubmitClick}>
              {t('student.student.button24')}
            </AntButton>
          </div>
        ]}
      >
        <div className="comm-table__area rowType">	
          <table>
            <colgroup>
              <col width="20%" />
              <col width="30%" />
              <col width="50%" />
            </colgroup>
            <tbody>
              <tr>
                <th className="text-center" colSpan={2}>
                  {t('student.student.column57')}
                </th>
                <th className="text-center">{t('student.student.column58')}</th>
              </tr>
              <tr>
                <td className="text-center">{t('student.student.column59')}</td>
                <td>
                  <Radio
                    disabled={Boolean(data)}
                    checked={sendType === 'GE'}
                    onChange={() => setSendType('GE')}
                    text={t('student.student.option20')}
                  />
                  <Radio
                    disabled={Boolean(data)}
                    className="ml-3"
                    checked={sendType === 'AD'}
                    onChange={() => setSendType('AD')}
                    text={t('student.student.option21')}
                  />
                </td>
                <td className="text-center">
                  {(data) ? 
                      <>
                        {(data?.sendGBN === 'AP') && 
                          <>
                            <input
                              disabled={true}
                              className="form-control"
                              type="text"
                              value={phone}
                              onChange={({ target: { value } }) => setPhone(value)}
                            />
                          </>
                        }  
                        {(data?.sendGBN === 'ME') && 
                          <>
                            <div className='d-flex comm-gap'>
                              <div className='w-90p text-left  ml-2'>
                                {data.phoneNumber === phone  ? parentName : 
                                  ((data.phoneNumber !== phone && data.phoneNumber !== studentPhone) ? '' : student?.studentKorName || '') }
                              </div>
                              <input
                                disabled={true}
                                className={"form-control " + ((data.phoneNumber !== phone && data.phoneNumber !== studentPhone) ? "" : "w-150p")}
                                type="text"
                                value={data.phoneNumber}
                                onChange={({ target: { value } }) => setPhone(value)}
                              />
                            </div>
                          </>
                        }  
                      </>
                    :
                    <>
                      <div className='d-flex comm-gap'>
                        <div className='w-90p text-left  ml-2'>
                          <Checkbox 
                            checked={isParentSend}
                            onChange={() => setIsParentSend(!isParentSend)}
                            text={parentName || ''}    
                          />
                        </div>
                        <input
                          disabled={Boolean(data)}
                          className="form-control w-120p"
                          type="text"
                          value={phone}
                          onChange={({ target: { value } }) => setPhone(value)}
                        />
                      </div>
                      <div className='d-flex comm-gap mt-2'>
                        <div className='w-90p text-left  ml-2'>
                          <Checkbox 
                            checked={isStudentSend}
                            onChange={() => setIsStudentSend(!isStudentSend)}
                            text={student?.studentKorName || ''}    
                          />
                        </div>
                        <input
                          disabled={Boolean(data)}
                          className="form-control w-120p"
                          type="text"
                          value={studentPhone}
                          onChange={({ target: { value } }) => setStudentPhone(value)}
                        />
                      </div>
                    </>
                  }
                </td>
              </tr>
              <tr>
                <td colSpan={3}>
                  <textarea
                    disabled={Boolean(data)}
                    rows={10}
                    value={content}
                    onChange={({ target: { value } }) => setContent(value)}
                  />
                </td>
              </tr>
              <tr>
                <td className="text-center">{t('sms.list.column3')}</td>
                <td colSpan={2}>
                  <div className="d-flex justify-content-between">
                    <div className="v-line">
                      <Radio
                        disabled={Boolean(data)}
                        checked={sendTimeDiv === 'IM'}
                        onChange={() => setSendTimeDiv('IM')}
                        text={t('student.student.option22')}
                      />
                      <Radio
                        disabled={Boolean(data)}
                        className="ml-3"
                        checked={sendTimeDiv === 'RE'}
                        onChange={() => setSendTimeDiv('RE')}
                        text={t('student.student.option23')}
                      />
                    </div>
                    <div className="date-picker__area">
                      <DatePicker
                        disabledDate={disabledDate}
                        disabled={sendTimeDiv === 'IM' || Boolean(data)}
                        onChange={onDateChange}
                        value={date}
                      />
                      <TimePicker
                        className="ml-3"
                        disabled={
                          sendTimeDiv === 'IM' || Boolean(data) || !Boolean(date)
                        }
                        disabledHours={disabledHours}
                        disabledMinutes={disabledMinutes}
                        onChange={onTimeChange}
                        value={time}
                        format="HH:mm"
                        minuteStep={10}
                      />
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td className="text-center">{t('student.student.column122')}</td>
                <td colSpan={2}>
                  <Radio
                    disabled={Boolean(data)}
                    checked={Boolean(data) ? (data?.sendGBN === 'AP' && true) : !sms}
                    onChange={() => setSms(false)}
                    text={t('student.student.option29')}
                  />
                  <Radio
                    disabled={Boolean(data)}
                    className="ml-3"
                    checked={Boolean(data) ? (data?.sendGBN === 'ME' && true) : sms}
                    onChange={() => setSms(true)}
                    text={t('student.student.option30')}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Modal>

      {confirmReserveVisible &&
        <ConfirmModal
          visible={confirmReserveVisible}
          setVisible={setConfirmReserveVisible}
          onConfirm={fnSubmit}
          titleText={t('student.student.title20')}
          messageText={`${date?.format(t('student.student.column123'))} ${time?.format(t('student.student.column124'))} ${t('student.student.text26')}`}
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
        />
      }
    </>
  );
};

export default SendMessageModal;
